import { Fragment, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { BombIcon, ChartIcon, ChevronIcon, CloseIcon, CopyIcon, DateRangePicker, EmptyState, EyeIcon, LoadingState, ErrorState, Pill, SearchIcon, Segments, useToast, UsersIcon } from "@signet/ui";
import { AccountTrend, ago, compact, NowStat, useMe, type AccountTrendFigure } from "@signet/web-kit";
import { BAN_REASON, fetchOverview, fetchStatus, keys, shortDay, signed, trendUrl, type Account, type Growth, type Overview } from "./api.ts";
import { AccountWho, CreatorChip, IgsPage, useIgsCreatorPhotos } from "./shared.tsx";
import type { CreatorPhotos } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   Growth: followers and views gained over the window picked (a day, a week,
   30 days or dates on the calendar), then by creator and by account. Only the
   growth itself: Jay dropped the comparison with the window before on
   2026-10-07.

   Banned accounts count in the totals for what they grew while they lived,
   and are listed apart under the live ones. An account newer than the window
   (or banned inside it) shows the days its figure covers, so a small number
   is never mistaken for a slow account (Jay, 2026-09-30). Each account also
   shows its followers when it was added beside its followers now, so growth
   since the start can be read off the row (Jay, 2026-10-07).
   ────────────────────────────────────────────────────────────────────────── */

type Span = "1" | "7" | "30";
type Search = { span?: string; from?: string; to?: string; creator?: string };

const SPANS: { value: Span; label: string }[] = [
  { value: "1", label: "Today" },
  { value: "7", label: "Week" },
  { value: "30", label: "30 days" },
];

/** Readings are filed under Melbourne days (scan.ts), so the window is counted back from Melbourne's today. */
function melbourneToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Melbourne" }).format(new Date());
}

const back = (day: string, days: number) => new Date(Date.parse(`${day}T00:00:00Z`) - days * 86_400_000).toISOString().slice(0, 10);

const range = (w: { from: string; to: string }) => `${shortDay(w.from)} to ${shortDay(w.to)}`;

/** Creators listed before anyone searches; the rest are a search or a click away (Jay, 2026-10-07). */
const CREATORS_SHOWN = 8;

function statusPill(a: Account) {
  if (a.state === "missing") return <Pill tone="bad">Not found</Pill>;
  if (a.state === "banned") return <Pill tone="idle">Banned</Pill>;
  if (a.sheetStatus === "WARMING UP") return <Pill tone="warn" dot>Warming up</Pill>;
  if (a.isPrivate) return <Pill tone="info">Private</Pill>;
  return <Pill tone="good" dot>Posting</Pill>;
}

/** The window a growth figure covers, said under it when the account was not read for all of it. */
function coverage(growth: Growth, windowFrom: string): string | null {
  if (!growth.partial || !growth.measuredTo) return null;
  return growth.measuredFrom && growth.measuredFrom !== windowFrom ? `${shortDay(growth.measuredFrom)} to ${shortDay(growth.measuredTo)}` : `to ${shortDay(growth.measuredTo)}`;
}

function growthFigure(label: string, value: number | null, growth: Growth, window: Overview["window"]): AccountTrendFigure {
  return {
    label,
    value: value === null ? (growth.measuredFrom ? "One reading" : "Not yet") : signed(value, compact) || "0",
    tone: value !== null && value > 0 ? "up" : value !== null && value < 0 ? "down" : undefined,
    caption: coverage(growth, window.from) ?? range(window),
  };
}

/**
 * Account, poster, status, followers at start and now. A click opens the rest
 * under the row: total views, the window's growth and the trend (Jay,
 * 2026-10-14). On 2026-10-20 the poster got its own column, and an account in
 * its warm-up or posting days says which day under its status. On 2026-11-02
 * Status moved before Poster (the boss), so a screenshot cropped after Status
 * can go to a creator without naming who posts for her.
 */
function AccountsTable({ rows, label, window, photos }: { rows: Account[]; label: string; window: Overview["window"]; photos: CreatorPhotos }) {
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (id: string) => setOpen((current) => (current === id ? null : id));
  return (
    <div className="table-scroll">
      <div className="table igs-accounts igs-centred" role="table" aria-label={label}>
        <div className="table-row head" role="row">
          <span role="columnheader">Account</span>
          <span role="columnheader">Status</span>
          <span role="columnheader">Poster</span>
          <span role="columnheader" title="Followers at the account's first reading, taken when it was added">Followers at start</span>
          <span role="columnheader">Followers now</span>
          <span role="columnheader" className="visually-hidden">Open</span>
        </div>
        {rows.map((a) => (
          <Fragment key={a.id}>
            <div
              className={`table-row clickable${open === a.id ? " selected" : ""}`}
              role="row"
              tabIndex={0}
              aria-expanded={open === a.id}
              onClick={(e) => ((e.target as HTMLElement).closest("a") ? undefined : toggle(a.id))}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " " ? (e.preventDefault(), toggle(a.id)) : undefined)}
            >
              <AccountWho
                id={a.id}
                handle={a.handle}
                photo={a.photo}
                sub={
                  <>
                    {a.creator ? <CreatorChip name={a.creator} photos={photos} size={13} /> : null}
                    {[a.creator ? "" : null, a.handle !== a.sheetHandle ? `sheet says @${a.sheetHandle}` : null].filter((x) => x !== null).join(" · ")}
                  </>
                }
              />
              <span role="cell" className="igs-status-cell">
                {statusPill(a)}
                {a.day ? <span className="igs-day-line">{a.day}</span> : null}
              </span>
              <span role="cell">{a.poster ?? <span className="faint">Unassigned</span>}</span>
              <span className="tabular" role="cell">
                {a.startFollowers === null ? <span className="faint">Not yet</span> : compact(a.startFollowers)}
                {a.startDay ? <span className="igs-was">{shortDay(a.startDay)}</span> : null}
              </span>
              <span className="tabular" role="cell">
                {a.followers === null ? <span className="faint">Not yet</span> : compact(a.followers)}
                {a.followers !== null && a.startFollowers !== null && a.followers !== a.startFollowers ? (
                  <span className={`igs-was ${a.followers > a.startFollowers ? "igs-up" : "igs-down"}`}>{signed(a.followers - a.startFollowers, compact)} since start</span>
                ) : null}
              </span>
              <span role="cell" className="igs-chevron" aria-hidden="true"><ChevronIcon size={13} direction={open === a.id ? "up" : "down"} /></span>
            </div>
            {open === a.id ? (
              <div role="row" className="igs-drop-row">
                <div role="cell">
                  <AccountTrend
                    trendUrl={trendUrl(a.id)}
                    figures={[
                      { label: "Total views", value: a.views === null ? (a.isPrivate ? "Private" : "Not yet") : compact(a.views), caption: "Every Reel, now" },
                      growthFigure("View growth", a.growth.views, a.growth, window),
                      growthFigure("Follower growth", a.growth.followers, a.growth, window),
                    ]}
                  />
                </div>
              </div>
            ) : null}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/** One creator's banned accounts, each with the last figures read before its ban. */
function BannedTable({ rows, photos }: { rows: Account[]; photos: CreatorPhotos }) {
  return (
    <div className="table-scroll">
      <div className="table igs-banned igs-centred" role="table" aria-label="Banned accounts">
        <div className="table-row head" role="row">
          <span role="columnheader">Account</span>
          <span role="columnheader">Banned</span>
          <span role="columnheader">Reason</span>
          <span role="columnheader" className="num" title="The last follower count read before the ban">Final followers</span>
          <span role="columnheader" className="num" title="The last total of Reel views read before the ban">Final views</span>
        </div>
        {rows.map((a) => (
          <div key={a.id} className="table-row" role="row">
            <AccountWho
              id={a.id}
              handle={a.handle}
              photo={a.photo}
              sub={
                <>
                  {a.creator ? <CreatorChip name={a.creator} photos={photos} size={13} /> : null}
                  {[a.creator ? "" : null, a.handler].filter((x) => x !== null).join(" · ")}
                </>
              }
            />
            <span role="cell" className="tabular">{a.bannedAt ? shortDay(a.bannedAt.slice(0, 10)) : <span className="faint">Unknown</span>}</span>
            <span role="cell">{a.banReason ? BAN_REASON[a.banReason] : <span className="faint">Not given</span>}</span>
            <span className="num tabular" role="cell">{a.followers === null ? <span className="faint">Never read</span> : compact(a.followers)}</span>
            <span className="num tabular" role="cell">{a.views === null ? <span className="faint">Never read</span> : compact(a.views)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function OverviewPage() {
  // A poster given only the Accounts tab lands here from the bar; send them on.
  if (!useMe().can("igsuicide.account:view")) return <Navigate to={"/ig-suicide/accounts" as never} replace />;
  return <GrowthPage />;
}

/**
 * Every live account's Instagram link, one a line, so a creator can be sent all of hers at once
 * (Ben, 2026-11-02: "One button that copies all links to all live accounts so i can send to creator").
 */
function CopyLinks({ rows }: { rows: Account[] }) {
  const toast = useToast();
  const copy = async () => {
    const text = rows.map((a) => `https://www.instagram.com/${a.handle}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied", `${rows.length} account link${rows.length === 1 ? " is" : "s are"} on the clipboard, one a line.`);
    } catch {
      toast.error("Couldn't copy", "The browser didn't allow it. Try again.");
    }
  };
  return (
    <button type="button" className="secondary-button compact" onClick={() => void copy()}>
      <CopyIcon size={13} /> Copy all links
    </button>
  );
}

function GrowthPage() {
  // Module routes are not in the router's typed path list, so this page reads its search loosely.
  const search = useSearch({ strict: false } as never) as Search;
  const navigate = useNavigate();
  const set = (patch: Partial<Search>) =>
    navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const key of Object.keys(next)) if (next[key] === undefined || next[key] === "") delete next[key];
        return next;
      }) as never,
      replace: true,
    });

  // Dates picked on the calendar win; otherwise a day, a week or 30 days back from today.
  const custom = Boolean(search.from && search.to);
  const span: Span = SPANS.some((option) => option.value === search.span) ? (search.span as Span) : "30";
  const today = melbourneToday();
  const from = custom ? search.from! : back(today, Number(span));
  const to = custom ? search.to! : today;

  const overview = useQuery({
    queryKey: keys.overview(from, to, search.creator ?? null),
    queryFn: () => fetchOverview(from, to, search.creator ?? null),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const status = useQuery({ queryKey: keys.status, queryFn: fetchStatus, refetchInterval: 60_000 });
  // The search at the top lists every creator, so it can switch from one already picked.
  const everyone = useQuery({
    queryKey: keys.overview(from, to, null),
    queryFn: () => fetchOverview(from, to, null),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: Boolean(search.creator),
  });
  const photos = useIgsCreatorPhotos();
  const [query, setQuery] = useState("");
  const [allCreators, setAllCreators] = useState(false);
  const o = overview.data;
  const s = status.data;
  const wanted = query.trim().toUpperCase();
  const matching = (o?.creators ?? []).filter((c) => !wanted || c.creator.toUpperCase().includes(wanted));
  const suggestions = wanted
    ? ((search.creator ? everyone.data?.creators : o?.creators) ?? []).filter((c) => c.creator.toUpperCase().includes(wanted)).slice(0, 8)
    : [];
  const creatorsShown = wanted || allCreators ? matching : matching.slice(0, CREATORS_SHOWN);
  const pick = (creator: string) => {
    setQuery("");
    set({ creator });
  };
  // Accounts are read once a day, so Today holds growth only for the ones read so far today.
  const readInWindow = o ? o.accounts.filter((a) => a.growth.measuredTo !== null && a.growth.measuredTo > o.window.from).length : 0;

  return (
    <IgsPage
      intro="Followers and Reel views gained by the throwaway accounts on the IG SUICIDE sheet. Live accounts are read once a day. A banned account keeps what it grew while it was live, so a ban never takes growth away."
      actions={
        <>
          {search.creator ? (
            <button type="button" className="secondary-button compact igs-filter" onClick={() => set({ creator: undefined })} aria-label={`Show every creator, not only ${search.creator}`}>
              <CreatorChip name={search.creator} photos={photos} /> <CloseIcon size={11} />
            </button>
          ) : null}
          <Segments label="Window" value={custom ? ("" as Span) : span} options={SPANS} onChange={(value) => set({ span: value === "30" ? undefined : value, from: undefined, to: undefined })} />
          <DateRangePicker
            value={{ from: search.from ?? null, to: search.to ?? null }}
            onChange={(v) => set({ from: v.from ?? undefined, to: v.to ?? undefined, span: undefined })}
            defaultLabel="Pick dates"
          />
        </>
      }
    >
      {s ? (
        <div className="igs-status">
          <span className={s.sheet.problem ? "igs-bad" : undefined}>
            {s.sheet.off
              ? `Sheet sync off${s.sheet.syncedAt ? `, accounts as of ${ago(s.sheet.syncedAt)}` : ""}`
              : s.sheet.problem ? `Sheet: ${s.sheet.problem}` : s.sheet.syncedAt ? `Sheet read ${ago(s.sheet.syncedAt)}` : "Sheet not read yet"}
          </span>
          <span className={s.scan.source ? "igs-bad" : undefined}>
            {s.scan.source ? `HikerAPI: ${s.scan.source}` : `${s.scan.readThisWeek} of ${s.scan.due} live accounts read today`}
          </span>
          {o ? <a href={o.sheetUrl} target="_blank" rel="noreferrer noopener">Open the sheet</a> : null}
        </div>
      ) : null}

      {overview.isLoading ? (
        <LoadingState />
      ) : overview.isError || !o ? (
        <ErrorState title="Couldn't load IG Suicide" onRetry={() => overview.refetch()} />
      ) : (
        <>
          <div className="igs-stats">
            <NowStat
              icon={<UsersIcon size={18} />}
              label="Follower growth"
              colour="var(--green)"
              value={o.totals.followers === null ? "No figures yet" : signed(o.totals.followers, (n) => n.toLocaleString()) || "0"}
              empty={o.totals.followers === null}
              caption={range(o.window)}
            />
            <NowStat
              icon={<EyeIcon size={18} />}
              label="View growth"
              colour="var(--violet)"
              value={o.totals.views === null ? "No figures yet" : signed(o.totals.views, compact) || "0"}
              empty={o.totals.views === null}
              caption={range(o.window)}
            />
            <NowStat
              icon={<ChartIcon size={18} />}
              label="Live accounts"
              colour="var(--steel)"
              value={o.active.toLocaleString()}
              caption={o.missing ? `${o.missing} waiting for a ban check` : "Posting or warming up"}
            />
            <NowStat
              icon={<BombIcon size={18} />}
              label="Banned in this window"
              colour="var(--wine)"
              value={o.bannedInWindow.toLocaleString()}
              caption="Their growth before the ban still counts"
            />
          </div>

          {o.firstReading === null ? (
            <p className="igs-note">No account has been read yet. The first readings arrive within a few hours of the scan starting, and growth needs two readings, so figures appear from the second day.</p>
          ) : o.firstReading > o.window.from ? (
            <p className="igs-note">Readings began on {shortDay(o.firstReading)}, so nothing before then is counted.</p>
          ) : !custom && span === "1" ? (
            <p className="igs-note">Each account is read once a day, so Today shows the growth of the {readInWindow} {readInWindow === 1 ? "account" : "accounts"} read so far today, each since its read the day before.</p>
          ) : null}

          {!search.creator && o.creators.length > 0 ? (
            <section className="panel section-gap">
              <div className="panel-head">
                <div>
                  <h2>By creator</h2>
                  <p>Search for a creator, or pick one here, to see only their accounts.</p>
                </div>
                {/* In the creators' own header, not the page's top bar (Jay, 2026-10-22). */}
                <div className="igs-find">
                  <label className="igs-search">
                    <SearchIcon size={13} />
                    <input
                      type="search"
                      value={query}
                      placeholder="Search creators"
                      aria-label="Search creators"
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => (e.key === "Enter" && suggestions[0] ? pick(suggestions[0].creator) : e.key === "Escape" ? setQuery("") : undefined)}
                    />
                  </label>
                  {wanted ? (
                    <div className="igs-suggest" role="listbox" aria-label="Matching creators">
                      {suggestions.length ? (
                        suggestions.map((c) => (
                          <button key={c.creator} type="button" role="option" aria-selected={false} onClick={() => pick(c.creator)}>
                            <CreatorChip name={c.creator} photos={photos} size={18} />
                          </button>
                        ))
                      ) : (
                        <p className="igs-empty">No creator matches "{query.trim()}".</p>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="panel-body flush">
                <div className="table-scroll">
                  <div className="table igs-creators igs-centred" role="table" aria-label="Creators">
                    <div className="table-row head" role="row">
                      <span role="columnheader">Creator</span>
                      <span role="columnheader" className="num">Live</span>
                      <span role="columnheader" className="num">Banned</span>
                      <span role="columnheader" className="num">Follower growth</span>
                      <span role="columnheader" className="num">View growth</span>
                      <span role="columnheader" className="num">Followers</span>
                    </div>
                    {creatorsShown.map((c) => (
                      <div
                        key={c.creator}
                        className="table-row"
                        role="row"
                        tabIndex={0}
                        onClick={() => pick(c.creator)}
                        onKeyDown={(e) => (e.key === "Enter" ? pick(c.creator) : undefined)}
                      >
                        <strong role="cell"><CreatorChip name={c.creator} photos={photos} size={22} /></strong>
                        <span className="num tabular" role="cell">{c.active}</span>
                        <span className="num tabular faint" role="cell">{c.banned}</span>
                        <span className="num tabular" role="cell">
                          <span className={(c.growth.followers ?? 0) > 0 ? "igs-up" : undefined}>{c.growth.followers === null ? "-" : signed(c.growth.followers, compact) || "0"}</span>
                        </span>
                        <span className="num tabular" role="cell">
                          {c.growth.views === null ? "-" : signed(c.growth.views, compact) || "0"}
                        </span>
                        <span className="num tabular" role="cell">{compact(c.followers)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {wanted && !matching.length ? <p className="igs-empty">No creator matches "{query.trim()}".</p> : null}
                {!wanted && matching.length > CREATORS_SHOWN ? (
                  <button type="button" className="text-button tiny accent igs-more" onClick={() => setAllCreators((all) => !all)}>
                    {allCreators ? "Show fewer" : `Show all ${matching.length} creators`}
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}

          <section className="panel section-gap">
            <div className="panel-head">
              <div>
                <h2 className="igs-heading">{search.creator ? <><CreatorChip name={search.creator} photos={photos} size={22} />&apos;s live accounts</> : "Live accounts"}</h2>
                {/* Ben, 2026-11-04: picking a creator should say how many accounts she has in all. */}
                {search.creator ? (
                  <p className="igs-total">
                    {o.accounts.length + o.bannedAll.length} account{o.accounts.length + o.bannedAll.length === 1 ? "" : "s"} in total: {o.accounts.length} live, {o.bannedAll.length} banned.
                  </p>
                ) : null}
                <p>Click an account for its total views, its growth from {range(o.window)} and its trend.</p>
              </div>
              {search.creator && o.accounts.length ? <CopyLinks rows={o.accounts} /> : null}
            </div>
            <div className="panel-body flush">
              {o.accounts.length ? (
                <AccountsTable rows={o.accounts} label="Live accounts" window={o.window} photos={photos} />
              ) : (
                <EmptyState icon={<UsersIcon size={18} />} title="No live accounts" body="Accounts marked POSTING or WARMING UP on the sheet show here." />
              )}
            </div>
          </section>

          {search.creator ? (
            <section className="panel section-gap">
              <div className="panel-head">
                <div>
                  <h2 className="igs-heading"><CreatorChip name={search.creator} photos={photos} size={22} />&apos;s banned accounts</h2>
                  <p>Every banned account, newest ban first, with the last followers and views read before the ban.</p>
                </div>
              </div>
              <div className="panel-body flush">
                {o.bannedAll.length ? (
                  <BannedTable rows={o.bannedAll} photos={photos} />
                ) : (
                  <EmptyState icon={<BombIcon size={18} />} title="No banned accounts" body={`None of ${search.creator}'s accounts has been banned.`} />
                )}
              </div>
            </section>
          ) : null}

          {/* With a creator picked, their banned accounts box above already lists every one. */}
          {o.banned.length && !search.creator ? (
            <section className="panel section-gap">
              <div className="panel-head">
                <div>
                  <h2>Banned during this window</h2>
                  <p>No longer read. What they grew before the ban is in the totals above.</p>
                </div>
              </div>
              <div className="panel-body flush">
                <AccountsTable rows={o.banned} label="Banned accounts" window={o.window} photos={photos} />
              </div>
            </section>
          ) : null}
        </>
      )}
    </IgsPage>
  );
}
